import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { Section, Container, Reveal, Eyebrow } from "@/components/Section";
import { useLang } from "@/contexts/LanguageContext";
import { getMaterials, staticImg } from "@/lib/api";

export default function MaterialsPage() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    getMaterials().then(setItems).catch(() => setItems([]));
  }, []);

  return (
    <div data-testid="materials-page">
      <Section testid="materials-hero" className="pt-32 md:pt-36">
        <Container>
          <div className="max-w-4xl">
            <Reveal><Eyebrow>{t.nav.materials}</Eyebrow></Reveal>
            <Reveal delay={0.1}>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.06] mt-6">
                {t.materials.hero_h1}
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-2xl text-base md:text-[17px] text-arven-offwhite/70 leading-relaxed">
                {t.materials.hero_sub}
              </p>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Container className="pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((m, i) => (
            <Reveal key={m.id} delay={0.05 * i}>
              <button
                onClick={() => setOpen(m)}
                className="group w-full text-left overflow-hidden rounded-2xl kbd-card transition-all duration-500"
                data-testid={`material-card-${m.slug}`}
              >
                <div className="aspect-square overflow-hidden relative">
                  <img
                    src={staticImg(m.image)}
                    alt={m.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-arven group-hover:scale-[1.06]"
                  />
                </div>
                <div className="p-6">
                  <h3 className="serif text-2xl">{lang === "fr" ? m.name : m.name_en}</h3>
                  <p className="mt-2 text-[13px] tracking-wider uppercase text-arven-gold">
                    {lang === "fr" ? m.short : m.short_en}
                  </p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </Container>

      {/* Dialog */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-arven-noir/85 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
          onClick={() => setOpen(null)}
          data-testid="material-dialog"
        >
          <div
            className="relative max-w-5xl w-full bg-arven-surface border border-white/10 rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpen(null)}
              className="absolute top-4 right-4 z-10 bg-arven-noir/60 p-2 rounded-full text-arven-offwhite hover:text-arven-gold transition-colors"
              data-testid="material-dialog-close"
            >
              <X size={18} />
            </button>
            <div className="aspect-square md:aspect-auto md:min-h-[420px] overflow-hidden">
              <img src={staticImg(open.image)} alt={open.name} className="w-full h-full object-cover" />
            </div>
            <div className="p-8 md:p-10 flex flex-col">
              <Eyebrow>{t.nav.materials}</Eyebrow>
              <h2 className="serif text-3xl md:text-4xl mt-4">{lang === "fr" ? open.name : open.name_en}</h2>
              <p className="mt-2 text-arven-gold text-[12px] tracking-[0.22em] uppercase">
                {lang === "fr" ? open.short : open.short_en}
              </p>
              <p className="mt-6 text-[15px] leading-relaxed text-arven-offwhite/75">
                {lang === "fr" ? open.description : open.description_en}
              </p>
              <div className="mt-8">
                <p className="label-eyebrow mb-3">{t.materials.advantages}</p>
                <ul className="space-y-2">
                  {(lang === "fr" ? open.advantages : open.advantages_en).map((a) => (
                    <li key={a} className="flex items-start gap-3 text-[14px] text-arven-offwhite/80">
                      <span className="mt-2 inline-block h-px w-4 bg-arven-gold" />
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      <Section className="bg-arven-anthracite border-t border-white/5 noise-overlay relative">
        <Container>
          <div className="max-w-3xl mx-auto text-center py-6 md:py-12">
            <Reveal>
              <h2 className="serif text-3xl md:text-4xl lg:text-5xl leading-tight">{t.materials.cta}</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex justify-center">
                <Link to="/rendez-vous" className="btn-gold">{t.common.appointment} <ArrowRight size={14} /></Link>
              </div>
            </Reveal>
          </div>
        </Container>
      </Section>
    </div>
  );
}
